// 程序合成的音效，不引入任何音频文件。
//
// 为什么不用 mp3/ogg：一个转盘不值得给仓库加二进制资产和授权来源问题；PWA 离线时
// 合成音仍然可用；而 chiptune 音色对一个儿童积分应用本来就是对的。
//
// 静音存 localStorage 而不落库：这是**每设备**偏好——孩子的平板要响，家长上班时
// 在手机上查进度不要响。同一个账号在两处应该有不同答案，落库反而错。

const KEY = 'habitpoint:muted'
let ctx: AudioContext | null = null

export function isMuted(): boolean {
  try {
    return localStorage.getItem(KEY) === '1'
  } catch {
    return false // 隐私模式下读不到：默认出声
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(KEY, muted ? '1' : '0')
  } catch {
    /* 写不了就算了，最坏后果是下次打开又响 */
  }
}

// AudioContext 必须在用户手势里创建/恢复——浏览器的自动播放策略。
// 打卡的声音是在网络响应回来之后才播的，那已经脱离了手势上下文，
// 所以另有 primeAudioOnFirstGesture() 提前把它准备好。
function audio(): AudioContext | null {
  if (isMuted()) return null
  try {
    ctx ??= new AudioContext()
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null // 老浏览器没有 AudioContext：转盘照转，只是没声音
  }
}

function blip(freq: number, durSec: number, type: OscillatorType = 'square', gain = 0.15, delaySec = 0): void {
  const ac = audio()
  if (!ac) return
  const t0 = ac.currentTime + delaySec
  const osc = ac.createOscillator()
  const g = ac.createGain()
  osc.type = type
  osc.frequency.setValueAtTime(freq, t0)
  // 指数衰减而不是直接切断：切断会有「啪」的爆音。
  g.gain.setValueAtTime(gain, t0)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + durSec)
  osc.connect(g).connect(ac.destination)
  osc.start(t0)
  osc.stop(t0 + durSec)
}

export function tick(): void {
  blip(1200, 0.03, 'square', 0.06)
}

export function coin(): void {
  blip(988, 0.08)
  blip(1319, 0.16, 'square', 0.15, 0.07)
}

export function fanfare(): void {
  ;[523, 659, 784, 1047].forEach((f, i) => blip(f, 0.22, 'triangle', 0.16, i * 0.09))
}

// 触感。iOS Safari 不支持 navigator.vibrate——特性检测后静默降级，
// 一个没有震动的转盘仍然是完整的转盘。
export function buzz(ms: number): void {
  if (isMuted()) return
  try {
    navigator.vibrate?.(ms)
  } catch {
    /* 忽略 */
  }
}

// 转盘减速的咔哒声：间隔按 k² 增长，听感就是「越来越慢」。
// 不去精确跟踪角速度——那要求音频与 CSS 缓动函数始终同步，而耳朵只需要那个趋势。
export function scheduleTicks(durationMs: number, count = 36): number[] {
  const weights = Array.from({ length: count }, (_, k) => (k + 1) ** 2)
  const total = weights.reduce((a, b) => a + b, 0)
  let acc = 0
  return weights.map((w) => (acc += (w / total) * durationMs))
}

// 在第一次任意用户手势时把 AudioContext 建起来。
// Chrome 只允许在手势处理里 resume；而打卡音要等网络响应，那时窗口已经关了。
export function primeAudioOnFirstGesture(): void {
  const prime = () => {
    audio()
    window.removeEventListener('pointerdown', prime)
    window.removeEventListener('keydown', prime)
  }
  window.addEventListener('pointerdown', prime)
  window.addEventListener('keydown', prime)
}
